import { Badge } from "@/components/ui";
import { type Matter } from "@/lib/types";
import { formatDate, formatUsersName } from "@/lib/utils/format";
import type { UserAccountStatus } from "@/lib/utils/user-account-status";

export function MatterPersonnelHistory({
  matter,
  statusFor,
}: {
  matter: Matter;
  statusFor: (userId: string) => UserAccountStatus | undefined;
}) {
  const past = [
    ...matter.teamConnections
      .filter((team) => team.dateTimeEnd)
      .map((team) => ({
        id: team.id,
        ended: team.dateTimeEnd ?? "",
        start: team.dateTimeStart,
        label: team.label,
        kind: "team" as const,
      })),
    ...matter.assignees
      .filter((assignee) => assignee.dateTimeEnd)
      .map((assignee) => ({
        id: assignee.id,
        ended: assignee.dateTimeEnd ?? "",
        start: assignee.dateTimeStart,
        label: formatUsersName(assignee.user) ?? "Unknown user",
        accountStatus: statusFor(assignee.user.id),
        role: assignee.role.label,
        kind: "person" as const,
      })),
  ].sort((left, right) => right.ended.localeCompare(left.ended));

  return (
    <details className="border-border rounded-lg border md:col-span-2">
      <summary className="hover:bg-muted cursor-pointer px-4 py-3 text-sm font-medium">
        Past personnel
      </summary>
      <div className="border-border space-y-2 border-t px-4 py-3 text-sm">
        {past.length === 0 && (
          <p className="text-muted-foreground">No past personnel.</p>
        )}
        {past.map((item) => (
          <div
            key={item.id}
            className="flex flex-wrap items-center justify-between gap-3"
          >
            {item.kind === "team" ? (
              <Badge variant="outline" className="bg-muted">
                {item.label}
              </Badge>
            ) : (
              <span className="flex min-w-0 items-center gap-2">
                <span className="truncate">{item.label}</span>
                {item.accountStatus && (
                  <Badge variant="muted">{item.accountStatus}</Badge>
                )}
                <Badge variant="outline">{item.role}</Badge>
              </span>
            )}
            <span className="text-muted-foreground">
              {formatDate(item.start)} - {formatDate(item.ended)}
            </span>
          </div>
        ))}
      </div>
    </details>
  );
}
